import React from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../../notifications';
import { Spinner, ToggleField } from '../../../shared/components';
import {
  EmailBrandAlignment,
  EmailBrandHeaderStyle,
  EmailBrandKit,
  EmailBrandKitInput,
  EmailBrandKitResponse,
  EmailBrandPreview,
  getEmailBranding,
  previewEmailBranding,
  removeEmailBrandLogo,
  updateEmailBranding,
  uploadEmailBrandLogo,
} from '../services/emailBrandingService';
import { EmailIcon } from './emailProviderIcons';
import { Chevron, ChipGroup, Field, StatusPill } from './emailFormParts';
import { EmailLayoutsEditor } from './EmailLayoutsEditor';
import { EmailSnippetsEditor } from './EmailSnippetsEditor';
import styles from './EmailConnectorTab.module.css';
import brandStyles from './EmailBrandingPanel.module.css';

interface EmailBrandForm {
  enabled: boolean;
  logoUrl: string;
  logoAltText: string;
  logoWidth: number;
  headerStyle: EmailBrandHeaderStyle;
  headerAlignment: EmailBrandAlignment;
  primaryColor: string;
  backgroundColor: string;
  contentBackgroundColor: string;
  textColor: string;
  fontFamily: string;
  companyName: string;
  footerText: string;
  address: string;
  websiteUrl: string;
  socialLinks: { network: string; url: string }[];
}

type ColorKey = 'primaryColor' | 'backgroundColor' | 'contentBackgroundColor' | 'textColor';

const COLOR_FIELDS: { key: ColorKey; label: string }[] = [
  { key: 'primaryColor', label: 'emailBrandPrimaryColor' },
  { key: 'backgroundColor', label: 'emailBrandBackgroundColor' },
  { key: 'contentBackgroundColor', label: 'emailBrandContentColor' },
  { key: 'textColor', label: 'emailBrandTextColor' },
];

const FONT_LABELS: Record<string, string> = {
  arial: 'Arial',
  helvetica: 'Helvetica',
  verdana: 'Verdana',
  tahoma: 'Tahoma',
  trebuchet: 'Trebuchet MS',
  georgia: 'Georgia',
  times: 'Times New Roman',
};

const NETWORK_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  x: 'X',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
};

const FALLBACK_FONTS = ['system', ...Object.keys(FONT_LABELS)];
const FALLBACK_NETWORKS = Object.keys(NETWORK_LABELS);
const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const PREVIEW_DEBOUNCE_MS = 450;

const formFromKit = (kit: EmailBrandKit): EmailBrandForm => ({
  enabled: kit.enabled,
  logoUrl: kit.logoUrl ?? '',
  logoAltText: kit.logoAltText ?? '',
  logoWidth: kit.logoWidth,
  headerStyle: kit.headerStyle,
  headerAlignment: kit.headerAlignment,
  primaryColor: kit.primaryColor,
  backgroundColor: kit.backgroundColor,
  contentBackgroundColor: kit.contentBackgroundColor,
  textColor: kit.textColor,
  fontFamily: kit.fontFamily,
  companyName: kit.companyName ?? '',
  footerText: kit.footerText ?? '',
  address: kit.address ?? '',
  websiteUrl: kit.websiteUrl ?? '',
  socialLinks: kit.socialLinks.map(link => ({ ...link })),
});

const inputFromForm = (form: EmailBrandForm): EmailBrandKitInput => ({
  ...form,
  socialLinks: form.socialLinks.filter(link => link.url.trim()).map(link => ({ network: link.network, url: link.url.trim() })),
});

/** Native colour pickers only accept #rrggbb; expand shorthand so the swatch matches the text box. */
const pickerValue = (value: string): string => {
  if (!HEX_COLOR.test(value)) {
    return '#000000';
  }
  const hex = value.slice(1);
  return hex.length === 3 ? `#${hex.split('').map(ch => ch + ch).join('')}` : value;
};

interface EmailBrandingPanelProps {
  canManage: boolean;
  onLayoutsChanged?: () => void;
}

export const EmailBrandingPanel: React.FC<EmailBrandingPanelProps> = ({ canManage, onLayoutsChanged }) => {
  const { t } = useTranslation(['companies', 'admin']);
  const { showToast } = useToast();

  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState<'kit' | 'layouts' | 'snippets'>('kit');
  const [response, setResponse] = React.useState<EmailBrandKitResponse | null>(null);
  const [form, setForm] = React.useState<EmailBrandForm | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [preview, setPreview] = React.useState<EmailBrandPreview | null>(null);
  const [previewLoading, setPreviewLoading] = React.useState(false);
  const [previewWidth, setPreviewWidth] = React.useState<'desktop' | 'mobile'>('desktop');
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const previewRequestRef = React.useRef(0);

  const applyResponse = React.useCallback((next: EmailBrandKitResponse) => {
    setResponse(next);
    setForm(formFromKit(next.brandKit));
  }, []);

  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      applyResponse(await getEmailBranding());
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : t('emailBrandLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const baseline = React.useMemo(() => (response ? formFromKit(response.brandKit) : null), [response]);
  const isDirty = Boolean(form && baseline && JSON.stringify(form) !== JSON.stringify(baseline));

  React.useEffect(() => {
    if (!open || !form) {
      return undefined;
    }
    const requestId = ++previewRequestRef.current;
    const timer = window.setTimeout(() => {
      setPreviewLoading(true);
      previewEmailBranding({ ...inputFromForm(form), enabled: true })
        .then(result => {
          if (previewRequestRef.current === requestId) {
            setPreview(result);
          }
        })
        .catch(() => {
          if (previewRequestRef.current === requestId) {
            setPreview(null);
          }
        })
        .finally(() => {
          if (previewRequestRef.current === requestId) {
            setPreviewLoading(false);
          }
        });
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [open, form]);

  const update = <K extends keyof EmailBrandForm>(key: K, value: EmailBrandForm[K]) =>
    setForm(prev => (prev ? { ...prev, [key]: value } : prev));

  const save = async () => {
    if (!form) {
      return;
    }
    setSaving(true);
    try {
      applyResponse(await updateEmailBranding(inputFromForm(form)));
      showToast(t('emailBrandSaved'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('emailBrandSaveFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (file: File | undefined) => {
    if (!file) {
      return;
    }
    setUploading(true);
    try {
      const next = await uploadEmailBrandLogo(file);
      setResponse(next);
      setForm(prev => (prev ? { ...prev, logoUrl: next.brandKit.logoUrl ?? '' } : formFromKit(next.brandKit)));
      showToast(t('emailBrandLogoUploaded'), 'success');
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('emailBrandLogoUploadFailed'), 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const removeLogo = async () => {
    if (response?.hasUploadedLogo) {
      setUploading(true);
      try {
        const next = await removeEmailBrandLogo();
        setResponse(next);
        setForm(prev => (prev ? { ...prev, logoUrl: '' } : formFromKit(next.brandKit)));
      } catch (error) {
        showToast(error instanceof Error ? error.message : t('emailBrandLogoUploadFailed'), 'error');
      } finally {
        setUploading(false);
      }
      return;
    }
    update('logoUrl', '');
  };

  const applyCompanyProfile = () => {
    if (!response) {
      return;
    }
    const { suggestions } = response;
    setForm(prev => (prev ? {
      ...prev,
      companyName: suggestions.companyName ?? prev.companyName,
      address: suggestions.address ?? prev.address,
      websiteUrl: suggestions.websiteUrl ?? prev.websiteUrl,
    } : prev));
  };

  const hasSuggestions = Boolean(response && (response.suggestions.companyName || response.suggestions.address || response.suggestions.websiteUrl));
  const fonts = response?.options.fonts.length ? response.options.fonts : FALLBACK_FONTS;
  const networks = response?.options.socialNetworks.length ? response.options.socialNetworks : FALLBACK_NETWORKS;
  const disabled = !canManage || saving;
  const bodyId = 'email-branding-body';

  const pill = !response
    ? <StatusPill tone="off">{t('emailSummaryOff')}</StatusPill>
    : response.brandKit.enabled
      ? <StatusPill tone="ok">{t('emailBrandOn')}</StatusPill>
      : <StatusPill tone="off">{t('emailBrandOff')}</StatusPill>;

  const colorField = (key: ColorKey, label: string) => {
    const value = form?.[key] ?? '';
    const invalid = !HEX_COLOR.test(value);
    return (
      <div key={key} className={styles.field}>
        <span>{t(label)}</span>
        <div className={brandStyles.colorRow}>
          <input
            type="color"
            className={brandStyles.colorSwatch}
            value={pickerValue(value)}
            onChange={event => update(key, event.target.value)}
            disabled={disabled}
            aria-label={t(label)}
          />
          <input
            className={`text-input ${invalid ? brandStyles.invalid : ''}`}
            value={value}
            onChange={event => update(key, event.target.value.trim())}
            disabled={disabled}
            maxLength={7}
            spellCheck={false}
            aria-invalid={invalid}
            aria-label={`${t(label)} (hex)`}
          />
        </div>
      </div>
    );
  };

  const renderBody = () => {
    if (loading && !form) {
      return <Spinner />;
    }
    if (loadError) {
      return (
        <div className="error">
          {loadError}{' '}
          <button type="button" className={styles.linkButton} onClick={() => { void load(); }}>{t('retry', { ns: 'admin', defaultValue: 'Retry' })}</button>
        </div>
      );
    }
    if (!form) {
      return null;
    }

    return (
      <div className={brandStyles.layout}>
        <div className={brandStyles.editor}>
          <ToggleField
            label={t('emailBrandEnabled')}
            hint={t('emailBrandEnabledHint')}
            checked={form.enabled}
            onChange={value => update('enabled', value)}
            disabled={disabled}
          />

          <h4 className={styles.subHeading}>{t('emailBrandLogoHeading')}</h4>
          <div className={brandStyles.logoRow}>
            <div className={brandStyles.logoBox} style={{ background: form.headerStyle === 'band' ? pickerValue(form.primaryColor) : pickerValue(form.backgroundColor) }}>
              {form.logoUrl ? <img src={form.logoUrl} alt={form.logoAltText || t('emailBrandLogoHeading')} /> : <EmailIcon id="brand" size={28} />}
            </div>
            <div className={brandStyles.logoActions}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/gif"
                className={brandStyles.hiddenInput}
                onChange={event => { void uploadLogo(event.target.files?.[0]); }}
                data-testid="email-brand-logo-input"
              />
              <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current?.click()} disabled={disabled || uploading}>
                {uploading ? t('savingEllipsis', { ns: 'admin' }) : t('emailBrandUploadLogo')}
              </button>
              {form.logoUrl ? (
                <button type="button" className={styles.linkButton} onClick={() => { void removeLogo(); }} disabled={disabled || uploading}>
                  {t('emailBrandRemoveLogo')}
                </button>
              ) : null}
              <span className="company-field-hint">{t('emailBrandLogoHint')}</span>
            </div>
          </div>

          <div className="company-form-grid">
            {!response?.hasUploadedLogo ? (
              <Field label={t('emailBrandLogoUrl')} hint={t('emailBrandLogoUrlHint')} wide>
                <input
                  className="text-input"
                  value={form.logoUrl}
                  onChange={event => update('logoUrl', event.target.value)}
                  placeholder="https://cdn.example.com/logo.png"
                  disabled={disabled}
                />
              </Field>
            ) : null}
            <Field label={t('emailBrandLogoAlt')}>
              <input
                className="text-input"
                value={form.logoAltText}
                onChange={event => update('logoAltText', event.target.value)}
                placeholder={form.companyName || 'Acme'}
                disabled={disabled}
              />
            </Field>
            <Field label={t('emailBrandLogoWidth', { width: form.logoWidth })}>
              <input
                type="range"
                min={response?.options.minLogoWidth ?? 40}
                max={response?.options.maxLogoWidth ?? 320}
                step={4}
                value={form.logoWidth}
                onChange={event => update('logoWidth', Number(event.target.value))}
                disabled={disabled}
              />
            </Field>
          </div>

          <h4 className={styles.subHeading}>{t('emailBrandHeaderHeading')}</h4>
          <div className={brandStyles.chipRow}>
            <ChipGroup<EmailBrandHeaderStyle>
              value={form.headerStyle}
              onChange={value => update('headerStyle', value)}
              disabled={disabled}
              ariaLabel={t('emailBrandHeaderHeading')}
              options={[
                { id: 'plain', label: t('emailBrandHeaderPlain'), icon: 'plain' },
                { id: 'band', label: t('emailBrandHeaderBand'), icon: 'band' },
              ]}
            />
            <ChipGroup<EmailBrandAlignment>
              value={form.headerAlignment}
              onChange={value => update('headerAlignment', value)}
              disabled={disabled}
              ariaLabel={t('emailBrandAlignment')}
              options={[
                { id: 'left', label: t('emailBrandAlignLeft'), icon: 'alignLeft' },
                { id: 'center', label: t('emailBrandAlignCenter'), icon: 'alignCenter' },
              ]}
            />
          </div>

          <h4 className={styles.subHeading}>{t('emailBrandColorsHeading')}</h4>
          <div className="company-form-grid">
            {COLOR_FIELDS.map(field => colorField(field.key, field.label))}
            <Field label={t('emailBrandFont')} hint={t('emailBrandFontHint')}>
              <select className="text-input" value={form.fontFamily} onChange={event => update('fontFamily', event.target.value)} disabled={disabled}>
                {fonts.map(font => <option key={font} value={font}>{font === 'system' ? t('emailBrandFontSystem') : FONT_LABELS[font] ?? font}</option>)}
              </select>
            </Field>
          </div>

          <div className={brandStyles.headingRow}>
            <h4 className={styles.subHeading}>{t('emailBrandFooterHeading')}</h4>
            {hasSuggestions && canManage ? (
              <button type="button" className={styles.linkButton} onClick={applyCompanyProfile} disabled={disabled}>
                {t('emailBrandUseCompanyProfile')}
              </button>
            ) : null}
          </div>
          <div className="company-form-grid">
            <Field label={t('emailBrandCompanyName')}>
              <input className="text-input" value={form.companyName} onChange={event => update('companyName', event.target.value)} disabled={disabled} />
            </Field>
            <Field label={t('emailBrandWebsite')}>
              <input className="text-input" value={form.websiteUrl} onChange={event => update('websiteUrl', event.target.value)} placeholder="https://example.com" disabled={disabled} />
            </Field>
            <Field label={t('emailBrandAddress')} wide>
              <textarea className="text-input" rows={2} value={form.address} onChange={event => update('address', event.target.value)} disabled={disabled} />
            </Field>
            <Field label={t('emailBrandFooterText')} hint={t('emailBrandFooterTextHint')} wide>
              <textarea className="text-input" rows={3} value={form.footerText} onChange={event => update('footerText', event.target.value)} disabled={disabled} />
            </Field>
          </div>

          <div className={brandStyles.headingRow}>
            <h4 className={styles.subHeading}>{t('emailBrandSocialHeading')}</h4>
            {canManage && form.socialLinks.length < networks.length ? (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => update('socialLinks', [
                  ...form.socialLinks,
                  { network: networks.find(network => !form.socialLinks.some(link => link.network === network)) ?? networks[0], url: '' },
                ])}
                disabled={disabled}
              >
                {t('emailBrandAddSocial')}
              </button>
            ) : null}
          </div>
          {form.socialLinks.length === 0 ? <p className={styles.muted}>{t('emailBrandNoSocial')}</p> : null}
          {form.socialLinks.map((link, index) => (
            <div key={index} className={brandStyles.socialRow}>
              <select
                className="text-input"
                value={link.network}
                onChange={event => update('socialLinks', form.socialLinks.map((item, i) => (i === index ? { ...item, network: event.target.value } : item)))}
                disabled={disabled}
                aria-label={t('emailBrandSocialNetwork')}
              >
                {networks.map(network => <option key={network} value={network}>{NETWORK_LABELS[network] ?? network}</option>)}
              </select>
              <input
                className="text-input"
                value={link.url}
                onChange={event => update('socialLinks', form.socialLinks.map((item, i) => (i === index ? { ...item, url: event.target.value } : item)))}
                placeholder="https://"
                disabled={disabled}
                aria-label={t('emailBrandSocialUrl')}
              />
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => update('socialLinks', form.socialLinks.filter((_, i) => i !== index))}
                disabled={disabled}
              >
                {t('emailBrandRemoveSocial')}
              </button>
            </div>
          ))}

          {canManage ? (
            <div className={brandStyles.actions}>
              <span className={`${styles.footerNote} ${isDirty ? styles.footerDirty : ''}`}>
                {isDirty ? t('emailUnsavedChanges') : t('emailAllSaved')}
              </span>
              {isDirty ? (
                <button type="button" className="btn btn-secondary" onClick={() => baseline && setForm(baseline)} disabled={saving}>
                  {t('emailDiscardChanges')}
                </button>
              ) : null}
              <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving || !isDirty}>
                {saving ? t('savingEllipsis', { ns: 'admin' }) : t('emailBrandSave')}
              </button>
            </div>
          ) : null}
        </div>

        <div className={brandStyles.previewPane}>
          <div className={brandStyles.previewHead}>
            <span className={brandStyles.previewTitle}>{t('emailBrandPreview')}</span>
            <ChipGroup<'desktop' | 'mobile'>
              value={previewWidth}
              onChange={setPreviewWidth}
              ariaLabel={t('emailBrandPreview')}
              options={[
                { id: 'desktop', label: t('emailBrandPreviewDesktop'), icon: 'desktop' },
                { id: 'mobile', label: t('emailBrandPreviewMobile'), icon: 'mobile' },
              ]}
            />
          </div>
          <div className={brandStyles.previewFrameWrap}>
            {preview ? (
              <iframe
                title={t('emailBrandPreview')}
                className={brandStyles.previewFrame}
                style={{ width: previewWidth === 'mobile' ? 375 : '100%' }}
                sandbox=""
                srcDoc={preview.html}
                data-testid="email-brand-preview"
              />
            ) : previewLoading ? <Spinner /> : <p className={styles.muted}>{t('emailBrandPreviewUnavailable')}</p>}
          </div>
          {preview ? (
            <p className={`${styles.small} ${preview.exceedsClipLimit ? brandStyles.warnText : ''}`}>
              {preview.exceedsClipLimit
                ? t('emailBrandPreviewTooLarge', { size: Math.round(preview.htmlBytes / 1024) })
                : t('emailBrandPreviewSize', { size: Math.max(1, Math.round(preview.htmlBytes / 1024)) })}
              {!form.enabled ? ` ${t('emailBrandPreviewDisabledNote')}` : ''}
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className={`${styles.step} ${brandStyles.panel}`} data-testid="email-branding-panel">
      <button
        type="button"
        className={styles.stepHead}
        onClick={() => setOpen(current => !current)}
        aria-expanded={open}
        aria-controls={bodyId}
      >
        <span className={styles.stepTitle}>
          <span className={brandStyles.icon}><EmailIcon id="brand" size={18} /></span>
          <span>
            <h3>{t('emailBrandHeading')}</h3>
            <span className={brandStyles.subtitle}>{t('emailBrandSubtitle')}</span>
          </span>
        </span>
        <span className={styles.stepMeta}>
          {pill}
          <Chevron open={open} />
        </span>
      </button>
      {open ? (
        <div id={bodyId} className={styles.stepBody}>
          <div className={brandStyles.tabs} role="tablist" aria-label={t('emailBrandHeading')}>
            {(['kit', 'layouts', 'snippets'] as const).map(id => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={tab === id}
                className={`${brandStyles.tab} ${tab === id ? brandStyles.tabOn : ''}`}
                onClick={() => setTab(id)}
              >
                {id === 'kit' ? t('emailBrandTabKit') : id === 'layouts' ? t('emailBrandTabLayouts') : t('emailBrandTabSnippets')}
              </button>
            ))}
          </div>
          {tab === 'kit' ? renderBody() : null}
          {tab === 'layouts' ? <EmailLayoutsEditor canManage={canManage} onLayoutsChanged={onLayoutsChanged} /> : null}
          {tab === 'snippets' ? <EmailSnippetsEditor canManage={canManage} /> : null}
        </div>
      ) : null}
    </div>
  );
};
